class SProductBundle extends HTMLElement {
  #listeners;
  #requests = new Map();
  #submitting = false;

  connectedCallback() {
    this.#listeners = new AbortController();
    const options = { signal: this.#listeners.signal };
    const choices = this.querySelector('[data-bundle-choices]');
    if (!choices) return;
    choices.hidden = false;
    this.addEventListener('change', event => {
      if (event.target.matches('input[type="radio"]')) this.#toggle();
      if (event.target.matches('[data-bundle-option]')) this.#updateRow(event.target.closest('[data-bundle-row]'), event.target.dataset.optionPosition);
    }, options);
    this.addEventListener('command', event => {
      if (event.command !== '--add-pair') return;
      event.preventDefault();
      this.#addPair();
    }, options);
    const single = this.querySelector('[data-bundle-single]');
    if (single) {
      this.#singleObserver = new MutationObserver(() => this.#updateSinglePrice());
      this.#singleObserver.observe(single, { childList: true, subtree: true });
    }
    this.#updateSinglePrice();
    this.#toggle();
  }

  #singleObserver;

  #updateSinglePrice() {
    const source = this.querySelector('[data-bundle-single] [data-bundle-single-source]');
    const target = this.querySelector('[data-bundle-single-price]');
    if (!source || !target) return;
    const cents = Number(source.dataset.price);
    if (!Number.isFinite(cents)) return;
    const money = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
    const text = money.format(cents / 100);
    if (target.textContent !== text) target.textContent = text;
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#singleObserver?.disconnect();
    for (const request of this.#requests.values()) request.abort();
    this.#requests.clear();
  }

  #toggle() {
    const pair = this.querySelector('input[value="pair"]').checked;
    this.querySelector('[data-bundle-panel]').hidden = !pair;
    this.querySelector('[data-bundle-single]').hidden = pair;
    this.#error('');
    this.#updateButton();
  }

  #variant(row) {
    try { return JSON.parse(row.querySelector('[data-bundle-variant]').textContent); }
    catch { return null; }
  }

  #lines() {
    const lines = new Map();
    const variants = new Map();
    for (const row of this.querySelectorAll('[data-bundle-row]')) {
      const variant = this.#variant(row);
      if (!variant?.id || !variant.available) throw new Error(this.dataset.unavailableMessage);
      const id = String(variant.id);
      variants.set(id, variant);
      const line = lines.get(id) || { merchandiseId: id, quantity: 0 };
      line.quantity += 1;
      lines.set(id, line);
    }
    for (const [id, line] of lines) {
      const variant = variants.get(id);
      if (line.quantity < variant.min || (variant.max != null && line.quantity > variant.max) || line.quantity % variant.step !== 0) throw new Error(this.dataset.quantityMessage);
    }
    return [...lines.values()];
  }

  #updateButton() {
    const button = this.querySelector('[data-bundle-add]');
    let valid = !this.#submitting && this.#requests.size === 0;
    try { this.#lines(); } catch { valid = false; }
    button.disabled = !valid;
    button.setAttribute('aria-busy', String(this.#submitting));
    this.#updatePrice();
  }

  #updatePrice() {
    const variants = [...this.querySelectorAll('[data-bundle-row]')].map(row => this.#variant(row));
    const valid = this.#requests.size === 0 && variants.length === 2 && variants.every(variant => variant?.available && Number.isInteger(variant.price) && variant.price >= 0);
    this.querySelectorAll('[data-bundle-price]').forEach(element => { element.hidden = !valid; });
    if (!valid) return;
    const original = variants.reduce((total, variant) => total + variant.price, 0);
    const discounted = variants.reduce((total, variant) => total + variant.price - Math.floor(variant.price * 5 / 100), 0);
    const money = new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD' });
    this.querySelectorAll('[data-bundle-original]').forEach(element => { element.textContent = money.format(original / 100); });
    this.querySelectorAll('[data-bundle-total]').forEach(element => { element.textContent = money.format(discounted / 100); });
  }

  async #updateRow(row, position) {
    this.#requests.get(row)?.abort();
    const controller = new AbortController();
    this.#requests.set(row, controller);
    row.setAttribute('aria-busy', 'true');
    this.#error('');
    this.#updateButton();
    const url = new URL(this.dataset.productUrl, location.origin);
    url.search = location.search;
    url.searchParams.delete('variant');
    url.searchParams.set('view', 'bundle-variant');
    url.searchParams.set('option_values', [...row.querySelectorAll('[data-bundle-option]')].map(select => select.value).join(','));
    try {
      const response = await fetch(url, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(this.dataset.errorMessage);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const content = html.querySelector('[data-bundle-row-content]');
      if (!content || this.#requests.get(row) !== controller) throw new Error(this.dataset.errorMessage);
      row.querySelector('[data-bundle-row-content]').replaceWith(content);
      row.querySelector(`[data-option-position="${CSS.escape(position)}"]`)?.focus();
      if (!this.#variant(row)?.available) this.#error(this.dataset.unavailableMessage);
    } catch (error) {
      if (controller.signal.aborted) return;
      row.querySelector('[data-bundle-variant]').textContent = '{}';
      this.#error(error.message || this.dataset.errorMessage);
    } finally {
      if (this.#requests.get(row) === controller) {
        this.#requests.delete(row);
        row.removeAttribute('aria-busy');
        this.#updateButton();
      }
    }
  }

  async #addPair() {
    if (this.#submitting || this.#requests.size) return;
    this.#error('');
    let lines;
    try { lines = this.#lines(); } catch (error) { this.#error(error.message); return; }
    this.#submitting = true;
    this.#updateButton();
    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart({ lines }, { event: { context: 'product', detail: { source: 's-product-bundle', productId: this.dataset.productId } } });
        if (result?.userErrors?.length) throw new Error(result.userErrors[0].message);
        await window.Shopify.actions.openCart?.();
      } else {
        const response = await fetch(this.dataset.cartAddUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items: lines.map(line => ({ id: line.merchandiseId, quantity: line.quantity })) }) });
        const result = await response.json();
        if (!response.ok) throw new Error(result.description || this.dataset.errorMessage);
        location.assign(this.dataset.cartUrl);
      }
    } catch (error) {
      this.#error(error.message || this.dataset.errorMessage);
    } finally {
      this.#submitting = false;
      this.#updateButton();
    }
  }

  #error(message) {
    const target = this.querySelector('[data-bundle-error]');
    target.textContent = message;
    target.hidden = !message;
  }
}

if (!customElements.get('s-product-bundle')) customElements.define('s-product-bundle', SProductBundle);
